import React from 'react';
import { Box, Card, LinearProgress, Typography, useTheme } from '@mui/material';

interface SetProgressCardProps {
  id: number;
  name: string;
  total: number;
  mastered: number;
  onClick: () => void;
}

export const SetProgressCard: React.FC<SetProgressCardProps> = ({
  id,
  name,
  total,
  mastered,
  onClick,
}) => {
  const theme = useTheme();
  const progressPercent = total > 0 ? Math.round((mastered / total) * 100) : 0;
  const isCompleted = progressPercent === 100;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: 2,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 4,
        },
        backgroundColor: 'background.paper',
        border: isCompleted ? `2px solid ${theme.palette.warning.main}` : 'none',
      }}
    >
      {/* Visual subtle backdrop matching progress */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: `${progressPercent}%`,
          backgroundColor: isCompleted ? 'warning.light' : 'primary.light',
          opacity: 0.1,
          transition: 'width 0.3s ease',
          zIndex: 0,
        }}
      />

      <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
          {name}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Progress
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: isCompleted ? 'warning.main' : 'primary.main' }}>
            {mastered}/{total} words ({progressPercent}%)
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'divider',
            '& .MuiLinearProgress-bar': {
              backgroundColor: isCompleted ? 'warning.main' : 'primary.main',
            },
          }}
        />
      </Box>
    </Card>
  );
};
